import { useState } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { useTasks } from '@/hooks/useTasks';
import { useAuth } from '@/hooks/useAuth';
import { useLeadDepartment, useAgentDepartment } from '@/hooks/useConfig';
import { useUserWorkflow, useWorkflows, useWorkflowsByDepartmentId } from '@/hooks/useWorkflows';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PriorityBadge } from '@/components/ui/priority-badge';
import { DaysSinceSubmissionBadge, getDaysSinceSubmission } from '@/components/ui/days-since-badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Stage } from '@/lib/supabase-types';
import { Task, TicketWithRelations } from '@/lib/types';
import { format } from 'date-fns';
import { GripVertical, Clock, User, Eye, Move, Trash2, X, ListTodo } from 'lucide-react';
import { cn, getErrorMessage } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const ALL_TICKETS_VALUE = '__all_tickets__';
const DRAG_TYPE_TICKET = 'application/x-tickettrax-ticket-id';
const DRAG_TYPE_TASK = 'application/x-tickettrax-task-id';
const SEL_PREFIX_TICKET = 'ticket:';
const SEL_PREFIX_TASK = 'task:';

const getTicketStageId = (ticket: TicketWithRelations): string | null => {
  const withLegacy = ticket as TicketWithRelations & { stage_id?: string | null };
  return withLegacy.stageId ?? withLegacy.stage_id ?? null;
};

const getTaskStageId = (task: Task): string | null => {
  const withLegacy = task as Task & { stage_id?: string | null };
  return withLegacy.stageId ?? withLegacy.stage_id ?? null;
};

export function KanbanPage() {
  const { user, isAdmin, isTeamLeader, isAgent } = useAuth();
  const { leadDepartmentId } = useLeadDepartment();
  const { agentDepartmentId } = useAgentDepartment();
  const departmentIdForBoard = isTeamLeader ? leadDepartmentId : isAgent ? agentDepartmentId : null;
  const ticketFilters = departmentIdForBoard ? { departmentId: departmentIdForBoard } : undefined;
  const { data: userWorkflow, isLoading: isWorkflowLoading } = useUserWorkflow(user?.id);
  const { workflows, isLoading: isWorkflowsLoading } = useWorkflows();
  const { data: departmentWorkflows = [], isLoading: isDepartmentWorkflowsLoading } = useWorkflowsByDepartmentId(departmentIdForBoard ?? undefined);
  const { tickets, stages, updateTicketStage, deleteTicket, isLoading: isTicketsLoading } = useTickets(ticketFilters);
  const { tasks, updateTaskStatus, deleteTask, isLoading: isTasksLoading } = useTasks();
  const isLoading = isTicketsLoading || isWorkflowLoading || (isAdmin && isWorkflowsLoading) || ((isTeamLeader || isAgent) && isDepartmentWorkflowsLoading) || ((isTeamLeader || isAgent) && isTasksLoading);
  const departmentWorkflow = (isTeamLeader || isAgent) && departmentWorkflows.length > 0 ? departmentWorkflows[0] : null;
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(ALL_TICKETS_VALUE);
  const [draggedTicket, setDraggedTicket] = useState<TicketWithRelations | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moveTargetStageId, setMoveTargetStageId] = useState<string>('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isBulkMoving, setIsBulkMoving] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);

  const handleDragStartTicket = (e: React.DragEvent, ticket: TicketWithRelations) => {
    setDraggedTicket(ticket);
    setDraggedTask(null);
    e.dataTransfer.setData(DRAG_TYPE_TICKET, ticket.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragStartTask = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    setDraggedTicket(null);
    e.dataTransfer.setData(DRAG_TYPE_TASK, task.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStage(null);

    const ticketId = e.dataTransfer.getData(DRAG_TYPE_TICKET);
    const taskId = e.dataTransfer.getData(DRAG_TYPE_TASK);

    if (ticketId) {
      if (draggedTicket && getTicketStageId(draggedTicket) !== stageId) {
        updateTicketStage.mutate({ ticketId: draggedTicket.id, stageId });
      }
      setDraggedTicket(null);
    } else if (taskId) {
      if (draggedTask && getTaskStageId(draggedTask) !== stageId) {
        updateTaskStatus.mutate({ id: draggedTask.id, stageId });
      }
      setDraggedTask(null);
    }
  };

  const toggleTicketSelection = (id: string) => {
    const key = SEL_PREFIX_TICKET + id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTaskSelection = (id: string) => {
    const key = SEL_PREFIX_TASK + id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleStageSelection = (stageId: string) => {
    const stageTickets = getTicketsForStage(stageId);
    const stageTasks = getTasksForStage(stageId);
    const ticketKeys = stageTickets.map((t) => SEL_PREFIX_TICKET + t.id);
    const taskKeys = stageTasks.map((t) => SEL_PREFIX_TASK + t.id);
    const allKeys = [...ticketKeys, ...taskKeys];
    const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedIds.has(k));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const getSelectedTicketIds = () =>
    Array.from(selectedIds).filter((s) => s.startsWith(SEL_PREFIX_TICKET)).map((s) => s.slice(SEL_PREFIX_TICKET.length));
  const getSelectedTaskIds = () =>
    Array.from(selectedIds).filter((s) => s.startsWith(SEL_PREFIX_TASK)).map((s) => s.slice(SEL_PREFIX_TASK.length));

  const clearSelection = () => {
    setSelectedIds(new Set());
    setMoveTargetStageId('');
  };

  const getTicketsForStage = (stageId: string) => {
    return ticketsForBoard.filter((t) => getTicketStageId(t) === stageId);
  };

  const getTasksForStage = (stageId: string) => {
    return tasksForBoard.filter((t) => getTaskStageId(t) === stageId);
  };

  const handleBulkMove = async () => {
    if (!moveTargetStageId || selectedIds.size === 0) return;
    const ticketIds = getSelectedTicketIds();
    const taskIds = getSelectedTaskIds();
    setIsBulkMoving(true);
    try {
      await Promise.all([
        ...ticketIds.map((ticketId) =>
          updateTicketStage.mutateAsync({ ticketId, stageId: moveTargetStageId })
        ),
        ...taskIds.map((id) => updateTaskStatus.mutateAsync({ id, stageId: moveTargetStageId })),
      ]);
      const parts = [];
      if (ticketIds.length) parts.push(`${ticketIds.length} ticket(s)`);
      if (taskIds.length) parts.push(`${taskIds.length} task(s)`);
      toast.success(`Moved ${parts.join(' and ')}`);
      clearSelection();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to move some items'));
    } finally {
      setIsBulkMoving(false);
    }
  };

  const handleBulkRemove = async () => {
    const ticketIds = getSelectedTicketIds();
    const taskIds = getSelectedTaskIds();
    const toRemoveTickets = isAdmin ? ticketIds : [];
    const toRemoveTasks = isTeamLeader ? taskIds : [];
    if (toRemoveTickets.length === 0 && toRemoveTasks.length === 0) return;
    setIsBulkRemoving(true);
    try {
      await Promise.all([
        ...toRemoveTickets.map((id) => deleteTicket.mutateAsync(id)),
        ...toRemoveTasks.map((id) => deleteTask.mutateAsync(id)),
      ]);
      const parts = [];
      if (toRemoveTickets.length) parts.push(`${toRemoveTickets.length} ticket(s)`);
      if (toRemoveTasks.length) parts.push(`${toRemoveTasks.length} task(s)`);
      toast.success(`Removed ${parts.join(' and ')}`);
      setShowRemoveConfirm(false);
      clearSelection();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to remove some items'));
    } finally {
      setIsBulkRemoving(false);
    }
  };

  // Resolve which workflow to show: admin picks; team leader/agent see department workflow; otherwise user workflow
  const effectiveWorkflow =
    isAdmin && selectedWorkflowId && selectedWorkflowId !== ALL_TICKETS_VALUE
      ? workflows.find((w) => w.id === selectedWorkflowId)
      : (isTeamLeader || isAgent)
        ? departmentWorkflow ?? userWorkflow ?? null
        : userWorkflow ?? null;

  // Determine which stages to display based on selected scope
  let displayStages = stages;
  const isAdminAllTickets = isAdmin && selectedWorkflowId === ALL_TICKETS_VALUE;

  if (isAdminAllTickets) {
    // Build a union of global stages + workflow stages so "All Tickets" still works
    // even when /config/stages is empty or partial.
    const stageMap = new Map<string, Stage>();
    [...stages].sort((a, b) => a.position - b.position).forEach((s) => stageMap.set(s.id, s));

    workflows.forEach((wf) => {
      [...(wf.workflowStages ?? [])]
        .sort((a, b) => a.position - b.position)
        .forEach((ws) => {
          if (!stageMap.has(ws.stage.id)) stageMap.set(ws.stage.id, ws.stage);
        });
    });

    displayStages = Array.from(stageMap.values()).sort((a, b) => a.position - b.position);
  } else if (effectiveWorkflow?.workflowStages?.length) {
    displayStages = [...effectiveWorkflow.workflowStages]
      .sort((a, b) => a.position - b.position)
      .map((ws) => ws.stage);
  } else {
    displayStages = [...stages].sort((a, b) => a.position - b.position);
  }

  const displayStageIds = new Set(displayStages.map((s) => s.id));
  const ticketsForBoard = tickets.filter((t) => displayStageIds.has(getTicketStageId(t) ?? ''));
  const tasksForBoard = (isTeamLeader || isAgent) ? tasks.filter((t) => displayStageIds.has(getTaskStageId(t) ?? '')) : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-muted-foreground">Loading board...</div>
      </div>
    );
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Kanban Board</h1>
          <p className="text-muted-foreground">
            Drag and drop tickets and tasks to update their stage
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <label htmlFor="workflow-select" className="text-sm font-medium text-foreground whitespace-nowrap">
              Workflow
            </label>
            <Select value={selectedWorkflowId} onValueChange={setSelectedWorkflowId}>
              <SelectTrigger id="workflow-select" className="w-[220px]">
                <SelectValue placeholder="Select workflow" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TICKETS_VALUE}>
                  All Tickets
                </SelectItem>
                {workflows.map((wf) => (
                  <SelectItem key={wf.id} value={wf.id}>
                    {wf.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3">
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <div className="flex items-center gap-2">
            <Select value={moveTargetStageId} onValueChange={setMoveTargetStageId}>
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue placeholder="Move to stage..." />
              </SelectTrigger>
              <SelectContent>
                {displayStages.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleBulkMove}
              disabled={!moveTargetStageId || isBulkMoving}
            >
              <Move className="h-4 w-4 mr-1" />
              Move
            </Button>
          </div>
          {((isAdmin && getSelectedTicketIds().length > 0) || (isTeamLeader && getSelectedTaskIds().length > 0)) && (
            <Button size="sm" variant="destructive" onClick={() => setShowRemoveConfirm(true)} disabled={isBulkRemoving}>
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={clearSelection}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
      )}

      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove selected</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const tCount = isAdmin ? getSelectedTicketIds().length : 0;
                const kCount = isTeamLeader ? getSelectedTaskIds().length : 0;
                const parts = [];
                if (tCount) parts.push(`${tCount} ticket(s)`);
                if (kCount) parts.push(`${kCount} task(s)`);
                if (parts.length === 0) return null;
                return <>Are you sure you want to remove {parts.join(' and ')}? This action cannot be undone.</>;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkRemove} disabled={isBulkRemoving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isBulkRemoving ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {displayStages.map((stage) => {
          const stageTickets = getTicketsForStage(stage.id);
          const stageTasks = getTasksForStage(stage.id);
          const isOver = dragOverStage === stage.id;
          const totalInStage = stageTickets.length + stageTasks.length;

          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-80"
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              <div
                className={cn(
                  'rounded-lg border-2 border-dashed transition-colors min-h-[600px]',
                  isOver ? 'border-primary bg-primary/5' : 'border-transparent'
                )}
              >
                {/* Stage Header */}
                <div
                  className="flex items-center justify-between p-3 rounded-t-lg mb-2"
                  style={{ backgroundColor: `${stage.color}15` }}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={
                        totalInStage > 0 &&
                        stageTickets.every((t) => selectedIds.has(SEL_PREFIX_TICKET + t.id)) &&
                        stageTasks.every((t) => selectedIds.has(SEL_PREFIX_TASK + t.id))
                      }
                      onCheckedChange={() => toggleStageSelection(stage.id)}
                      aria-label={`Select all in ${stage.name}`}
                      className="shrink-0"
                    />
                    <span
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    <h3 className="font-semibold text-sm">{stage.name}</h3>
                  </div>
                  <span className="text-xs font-medium bg-background px-2 py-0.5 rounded-full">
                    {totalInStage}
                  </span>
                </div>

                {/* Tickets */}
                <div className="space-y-3 px-1">
                  {stageTickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      stage={stage}
                      onDragStart={handleDragStartTicket}
                      isDragging={draggedTicket?.id === ticket.id}
                      selected={selectedIds.has(SEL_PREFIX_TICKET + ticket.id)}
                      onToggleSelect={() => toggleTicketSelection(ticket.id)}
                    />
                  ))}

                  {/* Tasks (TASK_FLOW: department workflow stages; team leader / agent only) */}
                  {stageTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      stage={stage}
                      onDragStart={handleDragStartTask}
                      isDragging={draggedTask?.id === task.id}
                      selected={selectedIds.has(SEL_PREFIX_TASK + task.id)}
                      onToggleSelect={() => toggleTaskSelection(task.id)}
                    />
                  ))}

                  {totalInStage === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No tickets or tasks
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Stage shape from workflow (id, name, color) used for cards */
type StageForCard = { id: string; name: string; color: string };

interface TaskCardProps {
  task: Task;
  stage: StageForCard;
  onDragStart: (e: React.DragEvent, task: Task) => void;
  isDragging: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}

function TaskCard({ task, stage, onDragStart, isDragging, selected, onToggleSelect }: TaskCardProps) {
  const assigneeNames = (task.assignees ?? [])
    .map((a) => (a as { user?: { profile?: { name?: string }; email?: string } }).user?.profile?.name ?? (a as { user?: { email: string } }).user?.email)
    .filter(Boolean)
    .join(', ') || '—';

  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      className={cn(
        'cursor-grab active:cursor-grabbing transition-all hover:shadow-md border-l-4',
        isDragging && 'opacity-50 scale-95',
        selected && 'ring-2 ring-primary',
        'border-l-primary/50'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {onToggleSelect && (
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={`Select task ${task.title}`}
              className="shrink-0"
            />
          )}
          <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
          <ListTodo className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-xs font-medium text-muted-foreground">Task</span>
          <span
            className="ml-auto text-xs font-medium px-2 py-0.5 rounded capitalize"
            style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
          >
            {task.priority}
          </span>
        </div>
        <p className="text-sm font-medium line-clamp-2 mb-2">
          {task.title}
        </p>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {task.description?.substring(0, 60)}
          {(task.description?.length ?? 0) > 60 && '...'}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span className="capitalize">{task.type?.replace(/_/g, ' ') ?? '—'}</span>
          {task.dueDate && (
            <span>{format(new Date(task.dueDate), 'MMM d')}</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <User className="h-3 w-3 shrink-0" />
          <span className="truncate">{assigneeNames}</span>
        </div>
        <Link
          to={`/admin/tasks/${task.id}`}
          className="flex items-center justify-center gap-1 mt-3 pt-3 border-t text-xs text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <Eye className="h-3 w-3" />
          View task
        </Link>
      </CardContent>
    </Card>
  );
}

interface TicketCardProps {
  ticket: TicketWithRelations;
  stage: StageForCard;
  onDragStart: (e: React.DragEvent, ticket: TicketWithRelations) => void;
  isDragging: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}

function TicketCard({ ticket, stage, onDragStart, isDragging, selected, onToggleSelect }: TicketCardProps) {
  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart(e, ticket)}
      className={cn(
        'cursor-grab active:cursor-grabbing transition-all hover:shadow-md',
        isDragging && 'opacity-50 scale-95',
        selected && 'ring-2 ring-primary'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            {onToggleSelect && (
              <Checkbox
                checked={selected}
                onCheckedChange={onToggleSelect}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={`Select ${ticket.ticketNumber}`}
                className="shrink-0"
              />
            )}
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium text-muted-foreground truncate">
              {ticket.ticketNumber}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <DaysSinceSubmissionBadge days={getDaysSinceSubmission(ticket)} />
            <PriorityBadge priority={ticket.priority} showIcon={false} />
          </div>
        </div>

        <p className="text-sm font-medium line-clamp-2 mb-3">
          {ticket.description.substring(0, 80)}
          {ticket.description.length > 80 && '...'}
        </p>

        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <span>{ticket.ticketType?.name}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs">{ticket.requesterName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs">
              {ticket.createdAt
                ? format(new Date(ticket.createdAt), 'MMM d')
                : 'N/A'}
            </span>
          </div>
        </div>

        {ticket.assignees && ticket.assignees.length > 0 && (
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-1 mt-3 pt-3 border-t">
              <div className="flex -space-x-2">
                {ticket.assignees.slice(0, 3).map((assignee) => {
                  const name = assignee.user?.profile?.name ?? 'Unknown';
                  return (
                    <Tooltip key={assignee.id}>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Avatar className="h-6 w-6 border-2 border-background cursor-default">
                            <AvatarImage src={assignee.user?.profile?.avatarUrl} />
                            <AvatarFallback className="text-[10px]">
                              {name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {name}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
              {ticket.assignees.length > 3 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground cursor-default">
                      +{ticket.assignees.length - 3}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {ticket.assignees.slice(3).map((a) => a.user?.profile?.name ?? 'Unknown').join(', ')}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        )}

        <Link
          to={`/admin/tickets/${ticket.id}`}
          className="flex items-center justify-center gap-1 mt-3 pt-3 border-t text-xs text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <Eye className="h-3 w-3" />
          View Details
        </Link>
      </CardContent>
    </Card>
  );
}